import * as z from 'zod/mini'
import {
  contactFields,
  customerNoteField,
  JoinPublicEventBody,
  JoinPublicReservationBody,
  refineWithKey,
  ReservationSlot,
} from '@seiri/shared/common'

/**
 * The details step of the public booking widget: the contact fields and the event sign-up's two
 * consents. It differs from `JoinPublicReservationBody`, which also carries the service and the slot
 * picked on the earlier steps, hence its own schema and `toJoinPublicReservationBody`. The terms
 * must be ticked; the marketing consent is optional and only ever grants (docs/consents.md).
 */
export const bookingFormModel = z.object({
  ...contactFields,
  /** "Poznámka pre prevádzku", shown only where the service offers it (`allowCustomerNote`). */
  note: customerNoteField,
  termsAgreed: z.boolean().check(refineWithKey('validation.consentRequired', (value: boolean) => value)),
  marketingConsent: z.boolean(),
})
export type BookingFormModel = z.infer<typeof bookingFormModel>

export const emptyBookingFormModel = (): BookingFormModel => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  note: '',
  termsAgreed: false,
  marketingConsent: false,
})

/**
 * The note as a body carries it: only where the event or the service offers the field, and only when something was
 * written. A field the visitor never saw sends nothing.
 */
const noteFor = (allowCustomerNote: boolean, note: string) => {
  const text = note.trim()
  return allowCustomerNote && text ? { note: text } : {}
}

/** What the earlier steps picked. A `null` place or employee is "Nezáleží": the API assigns one. */
export interface BookingChoice {
  reservationBlueprintId: string
  slot: Pick<ReservationSlot, 'startDateTime' | 'endDateTime'>
  placeId: string | null
  employeeId: string | null
  /** The service offers "Poznámka pre prevádzku". */
  allowCustomerNote: boolean
}

export const toJoinPublicReservationBody = (
  model: BookingFormModel,
  choice: BookingChoice,
): JoinPublicReservationBody => {
  if (!model.termsAgreed) throw new Error('termsAgreed is false; the form should not have submitted')
  return {
    firstName: model.firstName,
    lastName: model.lastName,
    email: model.email,
    phone: model.phone,
    ...noteFor(choice.allowCustomerNote, model.note),
    termsAgreed: true,
    marketingConsent: model.marketingConsent,
    reservationBlueprintId: choice.reservationBlueprintId,
    ...(choice.placeId && { placeId: choice.placeId }),
    ...(choice.employeeId && { employeeId: choice.employeeId }),
    startDateTime: new Date(choice.slot.startDateTime).toISOString(),
    endDateTime: new Date(choice.slot.endDateTime).toISOString(),
  }
}

/**
 * The public event sign-up's form: the contact and the note. Its consents are kept beside it, as the sign-up step has
 * always held them, and joined in by `toJoinPublicEventBody`.
 */
export const eventSignUpFormModel = z.object({ ...contactFields, note: customerNoteField })
export type EventSignUpFormModel = z.infer<typeof eventSignUpFormModel>

export const emptyEventSignUpFormModel = (): EventSignUpFormModel => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  note: '',
})

export const toJoinPublicEventBody = (
  model: EventSignUpFormModel,
  { allowCustomerNote, marketingConsent }: { allowCustomerNote: boolean; marketingConsent: boolean },
): JoinPublicEventBody => ({
  firstName: model.firstName,
  lastName: model.lastName,
  email: model.email,
  phone: model.phone,
  ...noteFor(allowCustomerNote, model.note),
  termsAgreed: true,
  marketingConsent,
})
